if (!customElements.get('hero-sync')) {
  class HeroSync extends HTMLElement {
    connectedCallback() {
      this._init();

      // Bind section reload listener once — prevents accumulation on editor re-init
      if (!this._onSectionLoad) {
        this._onSectionLoad = (e) => {
          if (e.target.contains(this)) {
            this._stopTimer();
            if (this._observer) this._observer.disconnect();
            this.classList.remove('is-ready');
            this._init();
          }
        };
        document.addEventListener('shopify:section:load', this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this._stopTimer();
      if (this._observer) this._observer.disconnect();
      if (this._abortController) this._abortController.abort();
      document.removeEventListener('shopify:section:load', this._onSectionLoad);
    }

    // ---- Initialisation -----------------------------------------------

    _init() {
      // Abort previous event listeners bound to `this` before re-querying DOM
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this._surfaces     = Array.from(this.querySelectorAll('.hero-sync__surface'));
      this._contents     = Array.from(this.querySelectorAll('.hero-sync__slide-content'));
      this._mediaSlides  = Array.from(this.querySelectorAll('.hero-sync__media-slide'));
      this._centerImages = Array.from(this.querySelectorAll('.hero-sync__center-image'));
      this._dots         = Array.from(this.querySelectorAll('.hero-sync__dot'));
      this._announcer    = this.querySelector('[data-hero-sync-announcer]');
      this._count        = this._surfaces.length;
      this._index        = 0;
      this._delay        = parseInt(this.dataset.autoplayDelay, 10) || 6000;
      this._autoplay     = this.dataset.autoplay !== 'false';
      this._paused       = false;
      this._manualPause  = false;
      this._timer        = null;
      this._reduced      = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.style.setProperty('--hero-sync-autoplay-delay', `${this._delay}ms`);

      // Enable wipe transition after first frame so slide 1 doesn't animate in on load
      requestAnimationFrame(() => this.classList.add('is-ready'));

      this._bindDots(signal);
      this._bindAutoplayBtn(signal);
      this._bindHoverFocus(signal);
      this._bindSwipe(signal);
      this._initIntersection();

      this._activate(0, false);

      if (this._autoplay && !this._reduced && this._count > 1) {
        this._startTimer();
      }
    }

    // ---- Navigation ---------------------------------------------------

    _bindDots(signal) {
      this._dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this._activate(i, true);
          if (this._autoplay && !this._manualPause) this._startTimer();
        }, { signal });
      });
    }

    _activate(index, announce = false) {
      const prev = this._index;
      this._index = index;

      // ── Surfaces (background colour cross-fade)
      this._surfaces.forEach((s, i) => s.classList.toggle('is-active', i === index));

      // ── Content slides (opacity cross-fade + aria-hidden)
      this._contents.forEach((c, i) => {
        const active = i === index;
        c.classList.toggle('is-active', active);
        c.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      // ── Center overlap images (opacity fade)
      this._centerImages.forEach((img, i) => {
        img.classList.toggle('is-active', i === index);
      });

      // ── Media slides (right-to-left wipe + aria-hidden)
      this._mediaSlides.forEach((s, i) => {
        s.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        if (i === index) return;
        if (i === prev) {
          s.classList.remove('is-active');
          s.classList.add('is-behind');
        } else {
          s.classList.remove('is-active', 'is-behind');
        }
      });

      // Reset incoming to clip-path: 100%, force reflow, then animate to 0%
      const incoming = this._mediaSlides[index];
      incoming.classList.remove('is-active', 'is-behind');
      void incoming.offsetWidth;
      incoming.classList.add('is-active');

      // ── Dots — not toggle buttons, so no aria-pressed; label reflects current state
      this._dots.forEach((d, i) => {
        const active = i === index;
        d.classList.toggle('is-active', active);
        d.setAttribute(
          'aria-label',
          active
            ? `Slide ${i + 1} of ${this._count}, current slide`
            : `Go to slide ${i + 1} of ${this._count}`
        );
        d.classList.remove('is-progressing');
        if (active && this._autoplay && !this._paused && !this._reduced) {
          void d.offsetWidth;
          d.classList.add('is-progressing');
        }
      });

      // ── Live region — only announce on user-initiated navigation (not autoplay)
      if (announce && this._announcer) {
        this._announcer.textContent = `Slide ${index + 1} of ${this._count}`;
      }
    }

    _next() { this._activate((this._index + 1) % this._count, true); }
    _prev() { this._activate((this._index - 1 + this._count) % this._count, true); }

    // ---- Autoplay -----------------------------------------------------

    _startTimer() {
      this._stopTimer();
      if (this._count < 2) return;

      // Call _activate directly — autoplay transitions don't announce to avoid screen reader noise
      this._timer = setTimeout(() => {
        this._activate((this._index + 1) % this._count, false);
        this._startTimer();
      }, this._delay);

      const dot = this._dots[this._index];
      if (dot && !this._reduced) {
        dot.classList.remove('is-progressing');
        void dot.offsetWidth;
        dot.classList.add('is-progressing');
      }
    }

    _stopTimer() {
      if (this._timer) { clearTimeout(this._timer); this._timer = null; }
      this._dots.forEach(d => d.classList.remove('is-progressing'));
    }

    _pause(manual = false) {
      if (manual) this._manualPause = true;
      this._paused = true;
      this._stopTimer();
    }

    _resume() {
      if (this._manualPause) return;
      this._paused = false;
      if (this._autoplay && !this._reduced) this._startTimer();
    }

    // ---- Pause / play button ------------------------------------------

    _bindAutoplayBtn(signal) {
      const btn = this.querySelector('.hero-sync__autoplay-btn');
      if (!btn) return;

      btn.setAttribute('aria-pressed', 'false');

      btn.addEventListener('click', () => {
        const nowPaused = !this._manualPause;
        nowPaused ? this._pause(true) : (this._manualPause = false, this._resume());
        btn.setAttribute('aria-pressed', nowPaused ? 'true' : 'false');
        btn.setAttribute('aria-label', nowPaused ? 'Resume autoplay' : 'Pause autoplay');
      }, { signal });
    }

    // ---- Hover / focus pause ------------------------------------------

    _bindHoverFocus(signal) {
      this.addEventListener('focusin',  () => this._pause(false), { signal });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this._resume();
      }, { signal });
    }

    // ---- Touch swipe --------------------------------------------------

    _bindSwipe(signal) {
      let startX = 0;
      let startY = 0;

      this.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }, { passive: true, signal });

      this.addEventListener('touchend', (e) => {
        const dx = e.changedTouches[0].clientX - startX;
        const dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 44) {
          dx < 0 ? this._next() : this._prev();
          if (this._autoplay && !this._manualPause) this._startTimer();
        }
      }, { passive: true, signal });
    }

    // ---- IntersectionObserver (pause when off-screen) -----------------

    _initIntersection() {
      if (!('IntersectionObserver' in window)) return;
      if (this._observer) this._observer.disconnect();

      this._observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this._manualPause) this._resume();
          } else {
            this._pause(false);
          }
        });
      }, { threshold: 0.25 });

      this._observer.observe(this);
    }
  }

  customElements.define('hero-sync', HeroSync);
}
